import React, { useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { useAccount } from '../../context/AccountContext';
import { GET_TINYCMS_ARTICLE_ADDRESS_REPAIRS } from '../../graphql/queries';
import {
  FIX_TINYCMS_ARTICLE_ADDRESSES,
  UNDO_TINYCMS_ARTICLE_ADDRESS_FIX,
} from '../../graphql/mutations';
import { useAddons } from './addons';
import { showInkToast } from './InkToast';
import { InkSection } from './shared';

/**
 * Article settings › Web addresses to fix (Round 44 §5, backlog d0f4b037):
 * addresses with a stray number left by a curly quote or dash in a feed title
 * (/article/spiral-8217-s-edge). A fix applies at once, with nothing to approve:
 * the old address sends readers and search engines to the new one, and Undo puts
 * it back. Nothing shows when there's nothing to fix.
 */

type AddressRepair = {
  id: string;
  title: string;
  live: boolean;
  state: 'fix' | 'waiting';
  address: string;
  newAddress: string;
};
type AddressRepairs = {
  items: AddressRepair[];
  total: number;
  toFix: number;
  drafts?: number;
};

const count = (n: number) => n.toLocaleString('en-US');
const addresses = (n: number) => `${count(n)} ${n === 1 ? 'address' : 'addresses'}`;

/** The list's last line: how much of it this is, and what Fix all covers. */
export function repairsFoot(shown: number, total: number, drafts = 0) {
  if (shown >= total) return null;
  const also = drafts ? `, including ${count(drafts)} ${drafts === 1 ? 'draft' : 'drafts'}` : '';
  const live = drafts ? `: ${drafts === 1 ? 'it goes' : 'they go'} live at the fixed address` : '';
  return `The first ${count(shown)} of ${count(total)}. Fix all covers every one${also}${live}.`;
}

export function useArticleAddressRepairs() {
  const { accountId } = useAccount();
  const q = useQuery<{ tinycmsArticleAddressRepairs: AddressRepairs }>(
    GET_TINYCMS_ARTICLE_ADDRESS_REPAIRS,
    { variables: { accountId }, skip: !accountId, fetchPolicy: 'cache-and-network' }
  );
  return q.data?.tinycmsArticleAddressRepairs || null;
}

export function ArticleAddressRepairs() {
  const { accountId } = useAccount();
  const { isOwner } = useAddons();
  const repairs = useArticleAddressRepairs();
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [fixedRows, setFixedRows] = useState<AddressRepair[]>([]);
  // The batch Fix all made, for the done card and its Undo.
  const [done, setDone] = useState<{ batchId: string; fixed: number } | null>(null);
  const keep = (cache: any, shape: AddressRepairs) =>
    cache.writeQuery({
      query: GET_TINYCMS_ARTICLE_ADDRESS_REPAIRS,
      variables: { accountId },
      data: { tinycmsArticleAddressRepairs: shape },
    });
  const [fixAddresses] = useMutation(FIX_TINYCMS_ARTICLE_ADDRESSES, {
    update: (cache, { data }) => keep(cache, data.fixTinycmsArticleAddresses),
  });
  const [undoFix] = useMutation(UNDO_TINYCMS_ARTICLE_ADDRESS_FIX, {
    update: (cache, { data }) => keep(cache, data.undoTinycmsArticleAddressFix),
  });

  const run = async <T,>(work: () => Promise<T>): Promise<T | null> => {
    setProblem(null);
    setBusy(true);
    try {
      return await work();
    } catch (err) {
      setProblem(err instanceof Error ? err.message : 'That didn’t work. Try again.');
      return null;
    } finally {
      setBusy(false);
    }
  };

  const undo = async (batchId: string, afterwards: () => void) => {
    const res = await run(() => undoFix({ variables: { accountId, batchId } }));
    const restored = res?.data?.undoTinycmsArticleAddressFix?.restored;
    if (restored === undefined) return;
    afterwards();
    showInkToast(
      restored === 1
        ? 'Undone. The address is back as it was.'
        : `Undone. The ${count(restored)} addresses are back as they were.`
    );
  };

  const fixAll = async () => {
    const res = await run(() => fixAddresses({ variables: { accountId } }));
    const out = res?.data?.fixTinycmsArticleAddresses;
    if (!out?.fixed) return;
    const batch = { batchId: out.batchId as string, fixed: out.fixed as number };
    setDone(batch);
    setFixedRows([]);
    showInkToast(
      `${addresses(batch.fixed)} fixed. The old ${batch.fixed === 1 ? 'one now redirects' : 'ones now redirect'}.`,
      { label: 'Undo', onClick: () => void undo(batch.batchId, () => setDone(null)) }
    );
  };

  const fixOne = async (item: AddressRepair) => {
    const res = await run(() => fixAddresses({ variables: { accountId, ids: [item.id] } }));
    const out = res?.data?.fixTinycmsArticleAddresses;
    if (!out?.fixed) return;
    setFixedRows((had) => [...had, item]);
    showInkToast(`Fixed. The old address of “${item.title}” now redirects.`, {
      label: 'Undo',
      onClick: () =>
        void undo(out.batchId, () => setFixedRows((had) => had.filter((r) => r.id !== item.id))),
    });
  };

  const left = repairs?.total || 0;
  const open = repairs?.items || [];
  if (!left && !fixedRows.length && !done) return null;
  const canFix = isOwner && !busy;
  const notAdmin = isOwner ? undefined : 'Only admins can do this.';
  const foot = repairsFoot(
    open.length + fixedRows.length,
    left + fixedRows.length,
    repairs?.drafts
  );

  return (
    <InkSection
      id="addresses"
      title="Web addresses to fix"
      description="A curly quote or dash in a title left a stray number in these addresses."
    >
      <div className="aax-body">
        {left ? (
          <>
            <div className="aax-summary">
              <div className="aax-summary-text">
                <div className="aax-head">{addresses(left)} to fix</div>
                <div className="aax-sub">
                  Fixing changes the address right away. Nothing to approve: the old address keeps
                  working and sends readers and search engines to the new one.
                </div>
              </div>
              <button
                type="button"
                className="ink-btn ink-btn-primary"
                disabled={!canFix}
                title={notAdmin}
                onClick={() => void fixAll()}
              >
                {busy ? 'Fixing…' : left === 1 ? 'Fix it' : `Fix all ${count(left)}`}
              </button>
            </div>
            <div className="aax-list">
              {[...fixedRows, ...open].map((item) => {
                const isFixed = fixedRows.some((r) => r.id === item.id);
                return (
                  <div className="aax-row" key={item.id}>
                    <div className="aax-row-text">
                      <div className="aax-row-title">
                        <span>{item.title}</span>
                        {item.live ? null : <span className="aax-draft">Draft</span>}
                      </div>
                      <div className="aax-row-move">
                        {item.address} → <span>{item.newAddress}</span>
                      </div>
                    </div>
                    {isFixed ? (
                      <span className="aax-fixed">✓ Fixed</span>
                    ) : (
                      <button
                        type="button"
                        className="ink-btn"
                        disabled={!canFix}
                        title={notAdmin}
                        aria-label={`Fix the address of ${item.title}`}
                        onClick={() => void fixOne(item)}
                      >
                        Fix
                      </button>
                    )}
                  </div>
                );
              })}
              {foot ? <div className="aax-foot">{foot}</div> : null}
            </div>
          </>
        ) : done ? (
          <div className="aax-done">
            <span className="aax-done-mark">✓</span>
            <div className="aax-summary-text">
              <div className="aax-head">{addresses(done.fixed)} fixed</div>
              <div className="aax-sub">
                Old addresses now send readers and search engines to the new ones.
              </div>
            </div>
            <button
              type="button"
              className="ink-link-btn"
              disabled={busy}
              onClick={() => void undo(done.batchId, () => setDone(null))}
            >
              Undo
            </button>
          </div>
        ) : (
          <div className="aax-list">
            {fixedRows.map((item) => (
              <div className="aax-row" key={item.id}>
                <div className="aax-row-text">
                  <div className="aax-row-title">
                    <span>{item.title}</span>
                  </div>
                  <div className="aax-row-move">
                    {item.address} → <span>{item.newAddress}</span>
                  </div>
                </div>
                <span className="aax-fixed">✓ Fixed</span>
              </div>
            ))}
          </div>
        )}
        {problem ? (
          <p className="ink-site-row-desc" role="alert">
            {problem}
          </p>
        ) : null}
      </div>
    </InkSection>
  );
}
